import React, { useState } from "react";
import { Modal, ModalHeader } from "reactstrap";
import { FiHelpCircle } from "react-icons/fi";
import RadarBienestar2 from "./Company/RadarBienestar2";

/**
 * GlobalScoreCard
 * Displays the company's global wellness KPI score alongside a RadarBienestar2
 * chart. Includes a month-over-month delta badge and a help modal explaining
 * how to interpret the radar.
 */
const GlobalScoreCard = ({
  globalScore,
  monthlyImprovement,
  radarValues,
  onScrollToDetail,
  loading = false,
  error = false,
}) => {
  const [showHelpModal, setShowHelpModal] = useState(false);

  return (
    <div className="bg-white rounded-2xl p-4 sm:p-5 shadow-sm border border-slate-100 flex flex-col sm:flex-row lg:flex-col xl:flex-row justify-between items-stretch gap-6 h-full">
      <div className="flex flex-col justify-between sm:w-[190px] sm:flex-shrink-0 lg:w-full lg:flex-shrink xl:w-[190px] xl:flex-shrink-0 py-1 text-left w-full space-y-4">
        <div>
          {/* Delta badge */}
          <div className="mb-2 flex justify-start">
            {error ? (
              <span className="inline-flex items-center gap-1 px-2.5 py-0.5 text-[10px] font-bold text-amber-600 bg-amber-50/70 border border-amber-100/50 rounded-full">
                Sin datos disponibles
              </span>
            ) : monthlyImprovement == null ? (
              <span className="inline-flex items-center gap-1 px-2.5 py-0.5 text-[10px] font-bold text-slate-400 bg-slate-50 border border-slate-100 rounded-full">
                Sin comparativa
              </span>
            ) : (() => {
              const isPositive = !String(monthlyImprovement).startsWith("-");
              return (
                <span
                  className={`inline-flex items-center gap-1 px-2.5 py-0.5 text-[10px] font-bold rounded-full border ${
                    isPositive
                      ? "text-emerald-600 bg-emerald-50/70 border-emerald-100/50"
                      : "text-red-500 bg-red-50/70 border-red-100/50"
                  }`}
                >
                  <svg
                    className={`w-2.5 h-2.5 ${isPositive ? "text-emerald-500" : "text-red-400"}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2.5"
                      d={
                        isPositive
                          ? "M5 10l7-7m0 0l7 7m-7-7v18"   // arrow up
                          : "M19 14l-7 7m0 0l-7-7m7 7V3"  // arrow down
                      }
                    />
                  </svg>
                  <span>{monthlyImprovement} pts vs mes anterior</span>
                </span>
              );
            })()}
          </div>

          {/* Title + help button */}
          <div className="flex items-center gap-2 mb-0.5">
            <span className="text-xl font-extrabold tracking-wider text-[#1d1f3f] uppercase font-display block text-left">
              PUNTUACIÓN GLOBAL
            </span>
            <button
              onClick={() => setShowHelpModal(true)}
              className="w-5 h-5 rounded-full bg-slate-50 hover:bg-slate-100 text-slate-400 hover:text-slate-600 flex items-center justify-center transition-colors border border-slate-150 p-0 cursor-pointer"
              title="¿Cómo leer este gráfico?"
              type="button"
            >
              <FiHelpCircle className="w-3.5 h-3.5" />
            </button>
          </div>

          {/* Score */}
          <div className="flex items-baseline justify-start gap-0.5 mt-1 font-display">
            {loading ? (
              <div className="h-12 w-24 rounded-lg bg-slate-100 animate-pulse" />
            ) : (
              <>
                <span className="text-5xl font-black text-slate-900 tracking-tight">
                  {globalScore ?? "—"}
                </span>
                <span className="text-2xl font-semibold text-slate-400">/100</span>
              </>
            )}
          </div>
        </div>

        {/* CTA */}
        <div className="pt-2 flex justify-start">
          <button
            onClick={onScrollToDetail}
            className="inline-flex items-center gap-1.5 px-3.5 py-2 bg-[#0075AC]/5 hover:bg-[#0075AC]/10 text-[#0075AC] text-[10px] font-bold rounded-xl transition-all border-0 shadow-sm cursor-pointer"
          >
            <span>Ver análisis detallado</span>
            <svg
              className="w-3.5 h-3.5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M9 5l7 7-7 7"
              />
            </svg>
          </button>
        </div>
      </div>

      {/* Radar chart */}
      <div className="flex-1 flex items-center justify-center w-full">
        {loading ? (
          <div className="w-full max-w-[240px] aspect-square rounded-full bg-slate-50 animate-pulse" />
        ) : (
          <RadarBienestar2 values={radarValues} />
        )}
      </div>

      {/* Help modal */}
      <Modal
        isOpen={showHelpModal}
        toggle={() => setShowHelpModal(false)}
        centered
        size="md"
      >
        <ModalHeader
          toggle={() => setShowHelpModal(false)}
          className="border-0 pb-0"
        >
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg bg-[#1D1F3F]/10 flex items-center justify-center text-[#1D1F3F]">
              <FiHelpCircle className="w-4 h-4" />
            </div>
            <span className="font-bold text-slate-900 text-lg font-display">
              ¿Cómo leer este gráfico?
            </span>
          </div>
        </ModalHeader>

        <div className="px-5 pb-6 pt-3 space-y-4 text-sm text-slate-600">
          <p>
            La <span className="font-semibold text-slate-800">Puntuación Global</span> es
            un KPI compuesto que combina cuatro dimensiones de bienestar de tu equipo
            en el período seleccionado.
          </p>

          {/* Radar dimensions */}
          <div className="rounded-xl border border-slate-100 overflow-hidden">
            <table className="w-full text-left text-xs">
              <thead>
                <tr className="bg-slate-50 text-slate-400 uppercase tracking-wider font-bold">
                  <th className="px-3 py-2">Dimensión</th>
                  <th className="px-3 py-2">Qué mide</th>
                  <th className="px-3 py-2 text-center">Mejor valor</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {[
                  { icon: "🧠", label: "Estrés",       desc: "Nivel promedio de estrés del equipo",       best: "Bajo" },
                  { icon: "👟", label: "Pasos",         desc: "Promedio de pasos diarios",                 best: "Alto" },
                  { icon: "🔄", label: "Consistencia",  desc: "Regularidad en el uso del dispositivo",     best: "Alto" },
                  { icon: "😴", label: "Sueño",         desc: "Horas promedio de sueño por noche",         best: "Alto" },
                ].map(({ icon, label, desc, best }) => (
                  <tr key={label} className="bg-white hover:bg-slate-50/60 transition-colors">
                    <td className="px-3 py-2.5 font-semibold text-slate-700 whitespace-nowrap">
                      <span className="mr-1.5">{icon}</span>{label}
                    </td>
                    <td className="px-3 py-2.5 text-slate-500">{desc}</td>
                    <td className="px-3 py-2.5 text-center">
                      <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-bold ${
                        best === "Bajo"
                          ? "bg-blue-50 text-blue-600"
                          : "bg-emerald-50 text-emerald-600"
                      }`}>
                        {best}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Score interpretation */}
          <div className="bg-slate-50 rounded-xl p-3 space-y-1.5">
            <p className="font-semibold text-slate-700 text-xs uppercase tracking-wider">
              Interpretación del score
            </p>
            <div className="flex gap-2 flex-wrap">
              {[
                { range: "0 – 49",  label: "Crítico",   color: "bg-red-100 text-red-600"     },
                { range: "50 – 69", label: "Regular",   color: "bg-amber-100 text-amber-600"  },
                { range: "70 – 84", label: "Bueno",     color: "bg-blue-100 text-blue-600"    },
                { range: "85 – 100",label: "Excelente", color: "bg-emerald-100 text-emerald-600" },
              ].map(({ range, label, color }) => (
                <span key={label} className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold ${color}`}>
                  <span className="font-normal opacity-75">{range}</span>
                  {label}
                </span>
              ))}
            </div>
          </div>

          {/* Delta explanation */}
          <p className="text-xs text-slate-400 leading-relaxed">
            El badge <span className="font-semibold text-slate-500">+/- pts vs período anterior</span> compara
            el score actual contra el período equivalente previo (misma duración, desplazado hacia atrás).
            Un valor positivo indica mejora; negativo, regresión.
          </p>
        </div>
      </Modal>
    </div>
  );
};

export default GlobalScoreCard;
